import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import bannerImg from "../assets/images/shutterstock_2655759041.jpg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowRightLong,
  faAward,
  faBath,
  faBed,
  faCommentAlt,
  faEnvelope,
  faLocationDot,
  faMagnifyingGlass,
  faPhone,
  faQuoteLeft,
  faQuoteRight,
  faRulerCombined,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, EffectFade, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/effect-fade";
import avatar1 from "../assets/images/avatar1.png";
import avatar2 from "../assets/images/avatar3.png";
import placeholder from "../assets/images/house-placeholder.webp";
import { Link } from "react-router-dom";
import imgs from "./temp.json";
import ReactImageGallery from "react-image-gallery";
import { motion } from "framer-motion";
import calc from "../assets/images/shutterstock_2489188913-min.jpg";
import report from "../assets/images/shutterstock_699442570-min.jpg";
import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import formattingFunctions from "../functions/formatting";
import SEO, { pageSEO } from "../components/seo.component";
import isReactSnap from "../utils/isReactSnap";
import globalState from "../utils/globalState";
import { useUserParams } from "../hooks/useUserParams";
import { buttonAnimation, fadeUp, stagger } from "../functions/animations";
import Contact from "./contact.component";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";

function toDisplayText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    return value.map(toDisplayText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    if (value.highlight != null) return toDisplayText(value.highlight);
    if (typeof value.text === "string") return value.text;
    if (typeof value.value === "string") return value.value;
    if (Array.isArray(value.content)) {
      return value.content.map(toDisplayText).filter(Boolean).join(" ");
    }
    for (const nested of Object.values(value)) {
      const text = toDisplayText(nested);
      if (text) return text;
    }
  }
  return "";
}

function toImageSrc(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.url || value.src || value.image || null;
  }
  return null;
}

function normalizeTestimonialsList(rawTestimonials) {
  let list;
  if (Array.isArray(rawTestimonials)) {
    list = rawTestimonials;
  } else if (typeof rawTestimonials === "string") {
    try {
      const parsed = JSON.parse(rawTestimonials);
      return normalizeTestimonialsList(parsed);
    } catch {
      return [];
    }
  } else if (rawTestimonials && typeof rawTestimonials === "object") {
    if (Array.isArray(rawTestimonials.items)) {
      list = rawTestimonials.items;
    } else if (Array.isArray(rawTestimonials.data)) {
      list = rawTestimonials.data;
    } else {
      list = Object.values(rawTestimonials).filter(
        (item) => item != null && typeof item !== "string"
      );
    }
  } else {
    return [];
  }

  return list
    .map((item, index) => {
      if (item == null) return null;
      if (typeof item === "string") {
        return {
          name: `Testimonial ${index + 1}`,
          quote: item,
          image: null,
          location: "",
        };
      }
      if (typeof item !== "object") return null;

      const quote = toDisplayText(
        item.quote ?? item.description ?? item.highlight ?? item.text
      );
      const name =
        toDisplayText(item.name) || `Testimonial ${index + 1}`;
      const location = toDisplayText(
        item.location ?? item.role ?? item.title
      );

      return {
        name,
        quote,
        image: toImageSrc(item.image ?? item.img),
        location,
      };
    })
    .filter((item) => item && (item.quote || item.name));
}

export default function Homepage() {
  const proxyUrl = "https://cors-anywhere.herokuapp.com/";
  const loaded = useRef(false);
  const {
    getCurrentAgentName,
    getCurrentAbout,
    getCurrentHeadshot,
    getCurrentTestimonials,
  } = useUserParams();

  const rawTestimonials = getCurrentTestimonials();
  const testimonials = useMemo(
    () => normalizeTestimonialsList(rawTestimonials),
    [rawTestimonials]
  );

  // State
  // const [listings, setListings] = useState([]); //for production
  const [listings, setListings] = useState(data.value); //for local testing
  const [media, setMedia] = useState(imgs.thumbs.value);
  const [formatted, setFormatted] = useState({});
  const [offset, setOffset] = useState(0);
  const [keyword, setKeyword] = useState("");

  function getData() {
    // Skip API calls during static generation
    if (isReactSnap()) {
      return Promise.resolve({ value: [] });
    }

    var body = {
      url: `https://query.ampre.ca/odata/Property?$filter=(ListOfficeKey eq '${constants.OFFICE_ID}' and StandardStatus eq 'Active')&$orderby=OriginalEntryTimestamp desc,ListingKey&$top=12`,
      needAuth: true,
      bearerToken: constants.TOKEN,
    };

    return new Promise(async (resolve, reject) => {
      try {
        let request = `odata/Property(Q)$filter=(ListOfficeKey(S)eq(S)\'${constants.OFFICE_ID}\'(S)and(S)StandardStatus(S)eq(S)\'Active\')(A)$orderby=OriginalEntryTimestamp(S)desc,ListingKey(A)$top=12`;


        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${request}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function getMedia(listings) {
    // Skip API calls during static generation
    if (isReactSnap() || !listings || listings.length === 0) {
      return Promise.resolve({ value: [] });
    }

    // this is for development
    //var temp = `https://query.ampre.ca/odata/Media?$filter=(`

    // this is for production
    // var temp = `odata/Media?$filter=(`

    if (constants.ENVIRONMENT === "local") {
      var temp = `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "', ";
      }
      temp = temp.slice(0, -2);
      temp =
        temp +
        ") and ImageSizeDescription eq 'Medium' and PreferredPhotoYN eq true";
    } else {
      var temp = `/odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(`;
      for (let l of listings) {
        temp = temp + "'" + l.ListingKey + "',";
      }
      temp = temp.slice(0, -1);
      temp =
        temp +
        ")(S)and(S)ImageSizeDescription(S)eq(S)'Medium'(S)and(S)PreferredPhotoYN(S)eq(S)true";
    }
    var body = {
      url: temp,
      needAuth: true,
      bearerToken: constants.TOKEN,
      // Accept: 'application/json',
      // Authorization: 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ2ZW5kb3IvdHJyZWIvNjUyMCIsImF1ZCI6IkFtcFVzZXJzUHJkIiwicm9sZXMiOlsiQW1wVmVuZG9yIl0sImlzcyI6InByb2QuYW1wcmUuY2EiLCJleHAiOjI1MzQwMjMwMDc5OSwiaWF0IjoxNzMwMjI1OTA1LCJzdWJqZWN0VHlwZSI6InZlbmRvciIsInN1YmplY3RLZXkiOiI2NTIwIiwianRpIjoiMGYyMjk3OTY1Y2NjN2U4MSIsImN1c3RvbWVyTmFtZSI6InRycmViIn0.BMvVrtwah8swIzW38UMs6yKF-HM4QO0bZu81blYx6J4'
    };

    return new Promise(async (resolve, reject) => {
      try {
        let data =
          constants.ENVIRONMENT === "production"
            ? await fetch(`https://${constants.DOMAIN}/api/${temp}`)
            : await fetch(`http://${constants.URL}/metaGet`, {
              method: "POST",
              body: JSON.stringify(body),
              headers: {
                "Content-Type": "application/json",
              },
            });

        if (data.ok) {
          let res = await data.json();
          resolve(res);
        } else {
          reject("Failed to fetch data");
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  function formatMedia(media) {
    var temp = {};

    for (let img of media) {
      if (temp[img.ResourceRecordKey]) {
        temp[img.ResourceRecordKey] = [...temp[img.ResourceRecordKey], img];
      } else {
        temp[img.ResourceRecordKey] = [img];
      }
    }
    return temp;
  }

  const getURLParameters = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      userId: urlParams.get("userId"),
    };
  };

  // Function to fetch template settings from MongoDB
  const fetchTemplateSettings = async (userId) => {
    try {
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(
        `https://agent.proagentsolutions.com/MongoDB/GetTemplateSettingsByUserId?userId=${userId}&apiKey=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const templateSettings = await response.json();
      console.log("Template settings fetched:", templateSettings);

      if (templateSettings) {
        const settingsData = templateSettings.data || templateSettings;

        const mappedUserData = {
          userId: userId,
          agentName: settingsData.agentName || settingsData.AGENT_NAME,
          agentTitle: settingsData.agentTitle || settingsData.AGENT_TITLE,
          agentNumber: settingsData.agentNumber || settingsData.AGENT_NUMBER,
          agentEmail: settingsData.agentEmail || settingsData.AGENT_EMAIL,
          brokerage: settingsData.brokerage || settingsData.BROKERAGE,
          ...settingsData,
        };

        globalState.setUserData(mappedUserData);

        console.log("Template settings data:", settingsData);
        console.log("Template settings mapped to user data:", mappedUserData);
      }

      return templateSettings;
    } catch (error) {
      console.error("Error fetching template settings:", error);
      return null;
    }
  };

  // Load template settings when userId is present in URL
  useEffect(() => {
    const urlParams = getURLParameters();
    if (urlParams.userId) {
      globalState.setUserId(urlParams.userId);
      fetchTemplateSettings(urlParams.userId).then(() => {
        console.log(
          "Global State Updated with Template Settings:",
          globalState.getDebugInfo()
        );
      });
      console.log("Global State Updated:", globalState.getDebugInfo());
    }
  }, []);

  // Data fetching effect with React Snap compatibility
  useEffect(() => {
    if (isReactSnap()) return; // Skip fetching during react-snap
    const isReactSnapEnv =
      typeof navigator !== "undefined" &&
      navigator.userAgent.includes("ReactSnap");
    const isBrowser = typeof window !== "undefined";

    if (isBrowser && !isReactSnapEnv && !loaded.current) {
      getData()
        .then((data) => {
          setListings(data.value);
          getMedia(data.value)
            .then((d) => {
              setMedia(d.value);
              setFormatted(formatMedia(d.value));
            })
            .catch((err) => {
              console.log(err);
            });
        })
        .catch((err) => {
          console.log(err);
        });
      loaded.current = true;
    }
  }, []);

  function slugify(str) {
    str = str.replace(/^\s+|\s+$/g, "");
    str = str.toLowerCase();
    str = str
      .replace(/[^a-z0-9 -]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
    return str;
  }

  const seoConfig = pageSEO.home();

  return (
    <>
      <SEO {...seoConfig} />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-start overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src={bannerImg}
            className="w-full h-full object-cover"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{
              duration: 8,
              ease: "easeOut",
            }}
          />
        </div>
        <div class="absolute inset-0 bg-black/20"></div>
        {/* Left Black Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-transparent"></div>

        {/* Floating Content Card */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="w-[1200px] mx-auto md:translate-x-0 md:translate-y-0 px-6 md:p-10 shadow-xl z-20 text-white"
        >
          <div className="flex items-center gap-4">
            <span className="uppercase tracking-[0.4em] text-lg font-semibold">
              Trusted Real Estate
            </span>
          </div>
          <h1 className="mt-5 font-serif text-5xl lg:text-6xl leading-[0.95] ">
            Helping You
            <br />
            Find Home.
          </h1>

          <div className="mt-5 flex flex-wrap gap-4">
            <Link
              to="/listings"
              className="btn-primary"
            >
              Explore Listings
            </Link>

            <Link
              to="/contact"
              className="btn-primary-outline"
            >
              Contact
            </Link>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section className="py-32 bg-[#fffdfa] overflow-hidden">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-16">
          <div className="grid lg:grid-cols-12 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5 relative"
            >
              <div className="relative">
                {/* Vintage Frame */}
                <div className="absolute -top-7 -left-7 h-full w-full border-[15px] border-[#d3d2d0]/70"></div>

                <img
                  src={getCurrentHeadshot()}
                  alt={getCurrentAgentName()}
                  className="relative h-[600px] w-full object-cover"
                />

                {/* Label */}
                <div className="absolute bottom-0 left-0 bg-[#d3d2d0] px-8 py-6">
                  <p className="font-serif text-4xl text-[#2b2925]">
                    15+
                  </p>

                  <p className="mt-1 text-xs uppercase tracking-[0.35em] text-[#6d655b]">
                    Years Of Excellence
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-7"
            >
              <div className="flex items-center gap-5 mb-8">
                <div className="h-[1px] w-16 bg-[#8b8172]"></div>

                <span className="text-xs uppercase tracking-[0.45em] text-[#6d655b]">
                  About
                </span>

              </div>

              <h2 className="font-serif text-5xl lg:text-6xl leading-[1.05] text-[#2b2925]">
                {getCurrentAgentName()}
              </h2>

              <p className="mt-10 max-w-2xl text-lg leading-9 text-[#5f594f] whitespace-pre-line">
                {getCurrentAbout()}
              </p>

              <Link
                to="/about"
                className="inline-flex mt-10 border border-[#8b8172] px-10 py-4 text-xs uppercase tracking-[0.35em] text-[#2b2925] transition-all duration-300 hover:bg-[#2b2925] hover:text-[#eee9df]"
              >
                Discover More
              </Link>

              {/* Stats */}
              <div className="mt-16 grid grid-cols-2 gap-12 max-w-lg">
                <div className="border-t border-[#8b8172]/40 pt-6">
                  <p className="font-serif text-5xl text-[#2b2925]">
                    250+
                  </p>

                  <span className="mt-3 block text-xs uppercase tracking-[0.3em] text-[#6d655b]">
                    Properties Sold
                  </span>
                </div>

                <div className="border-t border-[#8b8172]/40 pt-6">
                  <p className="font-serif text-5xl text-[#2b2925]">
                    98%
                  </p>

                  <span className="mt-3 block text-xs uppercase tracking-[0.3em] text-[#6d655b]">
                    Client Trust
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Listings Section */}
      <motion.section
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="bg-white border-b border-gray-200"
      >
        <div className="mx-auto max-w-[1200px] px-6 py-28 lg:px-16">
          {/* Heading */}
          <div className="mb-16 text-center">
            <motion.p
              variants={fadeUp}
              className="mb-5 text-xs uppercase tracking-[0.5em] text-black/40"
            >
              Featured Collection
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl text-[#1f1f1f]"
            >
              Exceptional Homes
            </motion.h2>

            <div className="mx-auto mt-8 h-px w-20 bg-black/20"></div>
          </div>

          {/* Listings */}
          <motion.div variants={fadeUp} className="py-5">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={15}
              slidesPerView={1}
              loop
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              breakpoints={{
                0: {
                  slidesPerView: 1
                },
                786: {
                  slidesPerView: 2
                },
                1280: {
                  slidesPerView: 3
                }
              }}
            >
              {listings?.slice(0, 6).map((prop) => (
                <SwiperSlide key={prop.ListingKey}>
                  <Link
                    to={`/listing/${prop.ListingKey}`}
                    className="group block"
                  >
                    <div className="bg-white/70 shadow-xl transition-all duration-500">
                      {/* Image */}
                      <div className="relative h-[250px] overflow-hidden bg-gray-100">
                        <img
                          // src={
                          //   formatted[prop.ListingKey]?.find((img) => img.Order === 0)?.MediaURL ??
                          //   formatted[prop.ListingKey]?.[0]?.MediaURL ??
                          //   placeholder
                          // }
                          src={prop.MainImgUrl} //dummy image
                          alt={`${prop.StreetNumber} ${prop.StreetName}`}
                          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                        />

                        {/* Price */}
                        <div className="absolute left-5 bottom-5">
                          <span className="bg-white px-5 py-3 text-lg font-serif text-[#1f1f1f] shadow-sm">
                            ${prop.ListPrice?.toLocaleString()}
                          </span>

                        </div>

                        {/* Property Type */}
                        <div className="absolute right-5 top-5">
                          <span className="bg-black/70 px-4 py-2 text-xs uppercase tracking-[0.25em] text-white">
                            {prop.PropertyType}
                          </span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="px-3 py-6 border border-gray-100">
                        <p className="flex items-center text-xs uppercase tracking-[0.25em] text-black/40">
                          <FontAwesomeIcon
                            icon={faLocationDot}
                            className="mr-2"
                          />
                          {prop.CityRegion ?? prop.CrossStreet}
                        </p>

                        <h3 className="mt-4 font-serif text-2xl text-[#1f1f1f]">
                          {prop.StreetNumber} {prop.StreetName} {prop.StreetSuffix}
                        </h3>

                        <div className="mt-6 flex items-center justify-between border-t border-black/10 pt-5">
                          <div className="flex gap-5 text-sm text-black/50">
                            {prop.PropertyType !== "Commercial" && (
                              <>
                                <span className="flex items-center gap-2">
                                  <FontAwesomeIcon icon={faBed} />
                                  {prop.BedroomsTotal || prop.BedroomsTotalInteger}
                                </span>

                                <span className="flex items-center gap-2">
                                  <FontAwesomeIcon icon={faBath} />
                                  {prop.BathroomsTotalInteger}
                                </span>
                              </>
                            )}

                            <span className="flex items-center gap-2">
                              <FontAwesomeIcon icon={faRulerCombined} />
                              {prop.LivingAreaRange ?? "--"} sq.ft.
                            </span>
                          </div>

                          <span className="text-xs uppercase tracking-[0.25em] text-black transition-all duration-300 group-hover:tracking-[0.4em]">
                            View
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>

          {/* CTA */}
          <motion.div
            variants={buttonAnimation}
            className="mt-16 flex justify-center"
          >
            <Link
              to="/listings"
              className="border border-black px-10 py-4 text-xs uppercase tracking-[0.35em] text-black transition-all duration-300 hover:bg-black hover:text-white"
            >
              View All Listings
              <FontAwesomeIcon icon={faArrowRight} className="ml-3" />
            </Link>

          </motion.div>

        </div>
      </motion.section>

      {/* Testimonials Section */}
      <section className="overflow-hidden max-w-[1200px] mx-auto py-28">
        {/* Heading */}
        <div className="px-6 pb-14 lg:px-16 text-center">
          <motion.p
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="mb-5 text-xs uppercase tracking-[0.5em] text-black/40"
          >
            Client Stories
          </motion.p>

          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="font-serif text-5xl lg:text-6xl leading-none text-[#1f1f1f]"
          >
            Trusted By Our Clients
          </motion.h2>

          <div className="mx-auto mt-8 h-px w-20 bg-black/20"></div>

        </div>
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <Swiper
            modules={[Autoplay]}
            loop
            autoplay={{
              delay: 6000,
              disableOnInteraction: false,
            }}
            slidesPerView={1}
            effect="fade"
          >

            {testimonials.map((testimonial, index) => (
              <SwiperSlide key={index}>
                <div
                  className="relative min-h-[300px] flex items-center bg-cover bg-center py-5"
                  style={{
                    backgroundImage: `url(${bannerImg || "/images/testimonial-bg.jpg"})`
                  }}
                >
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/85"></div>

                  {/* Content */}
                  <div className="relative z-10 mx-auto w-full flex flex-col lg:flex-row gap-10 px-6 lg:px-16">

                    {/* Quote Mark */}
                    <motion.div
                      variants={fadeUp}
                      className="flex items-start"
                    >
                      <span className="font-serif text-[100px] leading-none text-white/20">
                        <FontAwesomeIcon icon={faQuoteRight} />
                      </span>
                    </motion.div>

                    {/* Testimonial */}
                    <motion.div
                      variants={fadeUp}
                      className="flex flex-col justify-center"
                    >
                      <p className="max-w-3xl font-serif text-2xl leading-[1.25] text-white">
                        "{testimonial.quote}"
                      </p>

                      <div className="mt-10 h-px w-24 bg-white/40"></div>

                      <div className="mt-8 flex items-center gap-5">
                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt={testimonial.name}
                            className="h-16 w-16 rounded-full object-cover border border-white/30"
                          />
                        ) : (
                          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/30 bg-white/10 text-xl font-serif text-white">
                            {testimonial.name
                              .split(" ")
                              .map(word => word[0])
                              .slice(0, 2)
                              .join("")
                              .toUpperCase()
                            }
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg uppercase tracking-[0.25em] text-white">
                            {testimonial.name}
                          </h3>

                          <p className="mt-2 text-sm uppercase tracking-[0.3em] text-white/60">
                            {testimonial.location}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>
      </section>

      {/* Useful Tools Section */}
      <section className="border-b border-cottage-secondary">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="py-28"
        >
          <div className="mx-auto max-w-[1200px] mb-14 px-6">
            <motion.div variants={fadeUp} className="mb-5">
              <p className="mb-5 text-xs uppercase tracking-[0.5em] text-black/40">
                Real Estate Resources
              </p>
            </motion.div>

            <motion.h2
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none text-[#1f1f1f]"
            >
              Useful Tools
            </motion.h2>

            <div className="mt-8 h-px w-20 bg-black/20"></div>
          </div>

          <motion.div variants={fadeUp} className="grid gap-1 md:grid-cols-3">
            {[
              {
                title: "Mortgage Calculator",
                description: "Plan your purchase with a clear understanding of your budget and monthly costs.",
                image: calc,
                link: "/calculator",
              },
              {
                title: "Market Reports",
                description: "Access valuable insights about pricing trends and your local real estate market.",
                image: report,
                link: "/real-estate-reports",
              },
              {
                title: "Home Evaluation",
                description: "Discover your home's current value with expert market analysis.",
                image: house,
                link: "/evaluation",
              },
            ].map((card, index) => (
              <Link
                key={index}
                to={card.link}
                className="group"
              >
                <div className="relative overflow-hidden bg-white  transition-all duration-500 hover:shadow-xl">

                  <div className="relative h-[400px] overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/60" />
                  </div>

                  <div className="absolute inset-0 flex flex-col p-8 text-white">
                    <p className="text-xs uppercase tracking-[0.35em]">
                      Discover
                    </p>

                    <h3 className="mt-4 font-serif text-3xl">
                      {card.title}
                    </h3>

                    <p className="mt-5 text-sm h-14 leading-5 text-white/70 line-clamp-3">
                      {card.description}
                    </p>


                    <div className="mt-auto flex items-center justify-between border-t border-white/30 pt-5">
                      <span className="text-xs uppercase tracking-[0.3em]">
                        Explore Tool
                      </span>

                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur group-hover:bg-white/50">
                        <FontAwesomeIcon icon={faArrowRight} />
                      </div>
                    </div>
                  </div>

                </div>
              </Link>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Contact />
    </>
  );
}
